import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ChevronRight, Gift, Sparkles, Play, Wallet, ShoppingBag, Wrench, MessageCircle, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppIcon from "@/components/AppIcon";
import ProstoHeader from "@/prosto/ProstoHeader";
import ProstoAppRow from "@/prosto/ProstoAppRow";

const TASKS = [
  { label: "Банки", cat: "Финансы", icon: Wallet, dark: true },
  { label: "Покупки", cat: "Шоппинг", icon: ShoppingBag, dark: false },
  { label: "Сервисы", cat: "Утилиты", icon: Wrench, dark: true },
  { label: "Общение", cat: "Соцсети", icon: MessageCircle, dark: false },
  { label: "Досуг", cat: "Игры", icon: Gamepad2, dark: true },
];

function firstLetter(name) {
  const ch = (name || "#").trim().charAt(0).toUpperCase();
  return /[A-ZА-ЯЁ0-9]/.test(ch) ? ch : "#";
}

export default function ProstoHome() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [apps, setApps] = useState([]);
  const letterRefs = useRef({});

  useEffect(() => {
    api.catalog().then(setCatalog).catch(() => setCatalog({ popular: [] }));
    api.apps({}).then(setApps).catch(() => setApps([]));
  }, []);

  const groups = useMemo(() => {
    const sorted = [...apps].sort((a, b) => a.name.localeCompare(b.name, "ru"));
    const map = {};
    sorted.forEach((a) => {
      const l = firstLetter(a.name);
      (map[l] = map[l] || []).push(a);
    });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b, "ru"))
      .map((letter) => ({ letter, items: map[letter] }));
  }, [apps]);

  const letters = groups.map((g) => g.letter);
  const popular = catalog?.popular || [];

  const scrollToLetter = (l) => {
    const el = letterRefs.current[l];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="pb-20">
      <ProstoHeader />

      <div className="px-4">
        {/* Search */}
        <button
          data-testid="prosto-search-input"
          onClick={() => navigate("/prosto/search")}
          className="mt-4 w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-slate-100 text-slate-400 active:scale-[0.99] transition"
        >
          <Search size={20} className="text-slate-400" />
          <span className="text-[15px]">Какое приложение нужно?</span>
        </button>

        {/* Промо — акция */}
        <motion.button
          data-testid="prosto-promo-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => navigate("/prosto/search")}
          className="mt-4 w-full text-left rounded-3xl bg-[#ECEBFF] p-4 flex items-center gap-3 active:scale-[0.99] transition"
        >
          <span className="shrink-0 w-11 h-11 grid place-items-center rounded-2xl bg-[#6C5CE7] text-white">
            <Sparkles size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-slate-900 leading-snug">
              Первое приложение — установка бесплатно
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Попробуйте, как это работает — без оплаты</div>
          </div>
          <ChevronRight size={20} className="text-slate-400 shrink-0" />
        </motion.button>

        {/* Рефералка */}
        <motion.button
          data-testid="prosto-referral-card"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          onClick={() => toast.info("Реферальная программа скоро — приглашайте друзей и зарабатывайте")}
          className="mt-3 w-full text-left rounded-3xl bg-[#FFF2C7] p-4 flex items-center gap-3 active:scale-[0.99] transition"
        >
          <span className="shrink-0 w-11 h-11 grid place-items-center rounded-2xl bg-[#F6B100] text-white">
            <Gift size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-slate-900 leading-snug">Пригласи друга — получи деньги</div>
            <div className="text-xs text-slate-600 mt-0.5">10% за покупки друзей · вывод от 500 ₽</div>
          </div>
          <ChevronRight size={20} className="text-slate-500 shrink-0" />
        </motion.button>

        {/* Под задачу */}
        <section className="mt-7">
          <h2 className="font-display text-xl font-extrabold text-slate-900 mb-3">Под задачу</h2>
          <div className="grid grid-cols-2 gap-2">
            {TASKS.map((t, i) => {
              const Icon = t.icon;
              const wide = i === TASKS.length - 1 && TASKS.length % 2 === 1;
              return (
                <button
                  key={t.label}
                  data-testid={`prosto-task-${t.cat}`}
                  onClick={() => navigate(`/prosto/category/${encodeURIComponent(t.cat)}`)}
                  className={`${wide ? "col-span-2" : ""} rounded-xl px-3 py-2 flex items-center gap-2 text-left active:scale-[0.98] transition ${
                    t.dark ? "bg-[#2F6BFF] text-white" : "bg-white border border-slate-200 text-slate-900"
                  }`}
                >
                  <span className={`shrink-0 flex items-center justify-center w-6 h-6 rounded-lg ${t.dark ? "bg-white/15" : "bg-[#2F6BFF]/10"}`}>
                    <Icon size={14} className={t.dark ? "text-white" : "text-[#2F6BFF]"} />
                  </span>
                  <span className="text-[13px] font-bold">{t.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Популярные */}
        <section className="mt-7">
          <h2 className="font-display text-xl font-extrabold text-slate-900 mb-3">Популярные</h2>
          <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
            {popular.map((app) => (
              <button
                key={app.id}
                data-testid={`prosto-popular-${app.id}`}
                onClick={() => navigate(`/prosto/app/${app.id}`)}
                className="shrink-0 w-[68px] active:opacity-70 transition"
              >
                <AppIcon src={app.icon} alt={app.name} size={64} className="rounded-[18px] border border-slate-200" />
                <div className="mt-1.5 text-[11px] text-slate-700 font-medium text-center leading-tight line-clamp-2">
                  {app.name}
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Баннер — как установить */}
        <button
          data-testid="prosto-install-banner"
          onClick={() => navigate("/prosto/guide")}
          className="mt-7 w-full text-left rounded-3xl bg-gradient-to-br from-[#2F6BFF] to-[#1E4FD6] p-5 flex items-center gap-4 active:scale-[0.99] transition shadow-xl shadow-blue-500/20"
        >
          <span className="shrink-0 w-12 h-12 grid place-items-center rounded-full bg-white/20 text-white">
            <Play size={22} className="fill-white ml-0.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[17px] font-bold text-white leading-snug">Как установить приложение</div>
            <div className="text-xs text-blue-100 mt-1">Посмотри подробную видео инструкцию - займет всего пару минут</div>
          </div>
        </button>

        {/* От А до Я */}
        <section className="mt-8 relative">
          <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">От А до Я</h2>
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              {groups.map((g) => (
                <div
                  key={g.letter}
                  ref={(el) => (letterRefs.current[g.letter] = el)}
                  className="scroll-mt-16"
                >
                  <div className="text-sm font-bold text-slate-400 mt-3 mb-1">{g.letter}</div>
                  <div className="divide-y divide-slate-100">
                    {g.items.map((app) => (
                      <ProstoAppRow key={app.id} app={app} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {/* Алфавитный индекс */}
            <div className="sticky top-20 self-start flex flex-col items-center gap-0.5 py-1">
              {letters.map((l) => (
                <button
                  key={l}
                  data-testid={`prosto-alpha-${l}`}
                  onClick={() => scrollToLetter(l)}
                  className="w-5 text-[11px] font-bold text-[#2F6BFF] leading-none py-0.5 active:scale-125 transition"
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
